import {DirectionType, TaskType, WellSurfaceDataShape} from 'constants/dataEnum';
import {PIPETTING_PARAMETERS_TIP_DIRECTIONAL_SETTINGS} from 'constants/pipettingParameterEnum';
import {cloneAndMapObject} from './objectHelper';

export const calculateTipPositionOffset = (direction, value, tipYPositionOffset, tipXPositionOffset, labware) => {
  const maxLimit = getMaxWellTipPositionValue(labware)?.x;
  const isPrevMinus = direction === DirectionType.DIRECTION_X ? tipYPositionOffset < 0 : tipXPositionOffset < 0;
  const absValue = Math.abs(value);
  const limit = Math.sqrt(maxLimit ** 2 - absValue ** 2);
  if (direction === DirectionType.DIRECTION_X) {
    const result = Math.abs(tipYPositionOffset) > limit ? limit : Math.abs(tipYPositionOffset);
    return {xOffset: value, yOffset: isPrevMinus ? -result : result};
  }

  const result = Math.abs(tipXPositionOffset) > limit ? limit : Math.abs(tipXPositionOffset);
  return {xOffset: isPrevMinus ? -result : result, yOffset: value};
};

export const getMaxWellTipPositionValue = (labware) => {
  const {Shape, Diameter, Length, Width} = labware?.wellSurfaceData || {};

  let result = {x: 0, y: 0, Shape};
  if (!Shape) return result;

  if (Shape === WellSurfaceDataShape.CIRCLE) {
    const maxLimit = Diameter / 2;
    result = {x: maxLimit, y: maxLimit, Shape};
  } else {
    result = {x: Width / 2, y: Length / 2, Shape};
  }

  return result;
};

export const getAllowedTipTouchOffDirectionChange = (labware) => {
  const isAllowDirectionX =
    !labware?.isTrough ||
    (labware?.isTrough && labware?.rowCount === 1 && labware?.colCount === 1) ||
    (labware?.isTrough && labware?.rowCount === 8);

  const isAllowDirectionY = !labware?.isTrough || (labware?.isTrough && labware?.colCount === 12);

  return {x: isAllowDirectionX, y: isAllowDirectionY};
};

export const setDefaultTipTouchOffDirectionTasks = (labware, task) => {
  const allowedTipTouchOffDirection = getAllowedTipTouchOffDirectionChange(labware);
  const maxWellPositionValue = getMaxWellTipPositionValue(labware);

  if (allowedTipTouchOffDirection.x && allowedTipTouchOffDirection.y) {
    // for circle and rectangle (non through) only.
    // Set touch direction default value into y position
    return {
      ...task,
      tipTouchOffDirectionX: maxWellPositionValue.x,
      tipTouchOffDirectionY: 0,
    };
  }
  if (!allowedTipTouchOffDirection.x) {
    // for landscape through (12).
    // Set touch direction default value into y position
    return {
      ...task,
      tipTouchOffDirectionX: 0,
      tipTouchOffDirectionY: maxWellPositionValue.y,
    };
  }
  if (!allowedTipTouchOffDirection.y) {
    // for portrait through (single, 8).
    // Set touch direction default value into x position
    return {
      ...task,
      tipTouchOffDirectionX: maxWellPositionValue.x,
      tipTouchOffDirectionY: 0,
    };
  }

  return {...task};
};

export const setAllPartOfTipTouchOffTask = (tasks, labwareState) =>
  Object.fromEntries(
    Object.entries(tasks).map(([key, task]) => {
      if (task.type === TaskType.ASPIRATE || task.type === TaskType.DISPENSE) {
        const selectedLabware = Object.values(labwareState).find((item) => item.LabwareID === task.labwareID);
        let partOfTipTouchOffTask = {};
        if (!task.tipTouchOffHeight) {
          partOfTipTouchOffTask = {
            ...task,
            tipTouchOffHeight: selectedLabware?.wellDepth,
          };
        }
        partOfTipTouchOffTask = setDefaultTipTouchOffDirectionTasks(selectedLabware, partOfTipTouchOffTask);
        return [key, {...task, ...partOfTipTouchOffTask}];
      }
      return [key, {...task}];
    }),
  );

export const omitTipDirectionalParameters = (pipettingParameters) => {
  const pipettingParameterFieldKeys = Object.keys(pipettingParameters).filter(
    (param) => !PIPETTING_PARAMETERS_TIP_DIRECTIONAL_SETTINGS.includes(param),
  );

  return cloneAndMapObject(pipettingParameters, pipettingParameterFieldKeys);
};
